import { supabase } from './supabase';
import { Question } from '../types';
import { notifyAdminOfPendingSession } from './adminAlert';
import { unlockOpenedForSession } from './referral';
import { buildWhatsAppTeaser } from './dispatchMessages';

// When a signed-out visitor fills out the "Start a Session" form and hits
// Send, we save their draft here, gate them into sign up / sign in, and
// auto-submit the real test the moment a session appears (see
// PendingTestAutoSubmit in App.tsx). This keeps the flow frictionless: they
// never have to re-enter anything after creating an account.
const KEY = 'yhuu_pending_test';

export interface PendingTestDraft {
  partner_name: string;
  partner_contact: string;
  partner_platform: string;
  questions: Question[];
  live_ai_enabled?: boolean;
}

export function savePendingTest(draft: PendingTestDraft) {
  try {
    localStorage.setItem(KEY, JSON.stringify(draft));
  } catch {
    // localStorage unavailable (e.g. private browsing) — draft just won't persist.
  }
}

export function getPendingTest(): PendingTestDraft | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as PendingTestDraft) : null;
  } catch {
    return null;
  }
}

export function clearPendingTest() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

// Must match CreateTest.tsx / Dashboard.tsx: only WhatsApp is auto-sent
// (send-message queues everything else for the admin). SMS used to be
// listed here too, which marked SMS sessions 'dispatched' without anything
// ever being sent.
function shouldSendInstantly(platform: string) {
  return platform === 'WhatsApp';
}

/**
 * Submits a previously-saved draft on behalf of a now-authenticated user.
 * Returns the new test's id on success, or null if there was nothing to
 * submit or the submission failed.
 */
export async function submitPendingTest(user: { id: string; email?: string | null }): Promise<string | null> {
  const draft = getPendingTest();
  if (!draft) return null;

  try {
    const instant = shouldSendInstantly(draft.partner_platform);
    const insertPayload = {
        creator_id: user.id,
        creator_email: user.email,
        partner_name: draft.partner_name,
        partner_contact: draft.partner_contact,
        partner_platform: draft.partner_platform,
        has_hints: false,
        status: instant ? 'dispatched' : 'pending',
        questions: draft.questions,
        ...(draft.live_ai_enabled ? { live_ai_enabled: true } : {}),
      };
    const { data, error } = await supabase
      .from('tests')
      .insert([insertPayload])
      .select()
      .single();

    if (error) throw error;

    // Background the slow parts — the session exists, so hand back the id now.
    void unlockOpenedForSession(data.id);

    if (instant) {
      void supabase.functions.invoke('send-message', {
        body: {
          to: draft.partner_contact,
          name: draft.partner_name,
          link: `https://yhuu.life/test/${data.id}`,
          channel: draft.partner_platform,
          teaser: buildWhatsAppTeaser(draft.questions?.[0]?.text),
        },
      }).catch(err => console.error('send-message failed:', err));
    } else {
      notifyAdminOfPendingSession(data.id);
    }

    clearPendingTest();
    return data.id as string;
  } catch (err) {
    console.error('Error auto-submitting pending test:', err);
    return null;
  }
}
